import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2, ArrowLeft, Eye, EyeOff, CheckCircle, AlertCircle, Lock, Check } from 'lucide-react';
import { apiClient } from '../../services/api/apiClient';
import toast from '../../utils/toast';
import { getImageUrl } from '../../utils/imageUtils';

interface ResetPasswordFormTWProps {
  onBackToLogin?: () => void;
}

interface ResetPasswordData {
  password: string;
  confirmPassword: string;
}

export const ResetPasswordFormTW: React.FC<ResetPasswordFormTWProps> = ({
  onBackToLogin,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState<ResetPasswordData>({
    password: '',
    confirmPassword: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isResetSuccessful, setIsResetSuccessful] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Logo-URL - zentrale Funktion für korrekte Umgebungserkennung
  const logoUrl = getImageUrl('/images/branding/renditefuchs-logo.svg');

  // If no token, show error
  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-sm space-y-10">
          {/* Logo */}
          <div>
            <Link to="/" aria-label="Zur Startseite" className="block">
              <img
                alt="Renditefuchs"
                src={logoUrl}
                className="mx-auto h-10 w-auto"
              />
            </Link>
            <h2 className="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900 dark:text-white">
              Ungültiger Link
            </h2>
          </div>

          <div className="space-y-6">
            <div className="p-4 rounded-lg bg-status-error/10 border border-red-500/50">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-status-error shrink-0 mt-0.5" />
                <p className="text-sm text-red-400">
                  Der Passwort-Reset-Link ist ungültig oder fehlt. Bitte fordern Sie einen neuen Link an.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate('/auth/forgot-password')}
              className="flex w-full justify-center items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm/6 font-semibold text-gray-900 hover:bg-brand-hover transition-colors"
            >
              Neuen Reset-Link anfordern
            </button>

            {onBackToLogin && (
              <button
                type="button"
                onClick={onBackToLogin}
                className="flex w-full justify-center items-center gap-2 text-sm text-brand hover:text-brand-hover transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Zurück zur Anmeldung
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.password.trim()) {
      errors.password = 'Passwort ist erforderlich';
    } else if (formData.password.length < 6) {
      errors.password = 'Passwort muss mindestens 6 Zeichen lang sein';
    }

    if (!formData.confirmPassword.trim()) {
      errors.confirmPassword = 'Passwort-Bestätigung ist erforderlich';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwörter stimmen nicht überein';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setFormErrors({});

    try {
      const response = await apiClient.resetPassword(token, formData.password);

      if (response.success) {
        setIsResetSuccessful(true);
        toast.success(response.message || 'Passwort erfolgreich zurückgesetzt!');
      } else {
        if (response.error?.includes('ungültig') || response.error?.includes('abgelaufen')) {
          setFormErrors({
            general: 'Der Reset-Link ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen an.'
          });
        } else {
          setFormErrors({ general: response.error || 'Fehler beim Zurücksetzen des Passworts' });
        }
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setFormErrors({ general: 'Fehler beim Zurücksetzen des Passworts. Bitte versuchen Sie es erneut.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (formErrors.general) {
      setFormErrors(prev => ({ ...prev, general: '' }));
    }
  };

  const isFormValid = () => {
    return formData.password.length >= 6 && formData.password === formData.confirmPassword;
  };

  // Success screen
  if (isResetSuccessful) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-sm space-y-10">
          {/* Logo */}
          <div>
            <Link to="/" aria-label="Zur Startseite" className="block">
              <img
                alt="Renditefuchs"
                src={logoUrl}
                className="mx-auto h-10 w-auto"
              />
            </Link>
            <h2 className="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900 dark:text-white">
              Passwort zurückgesetzt
            </h2>
          </div>

          <div className="space-y-6">
            <div className="p-4 rounded-lg bg-status-success/10 border border-green-500/50">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-status-success shrink-0 mt-0.5" />
                <p className="text-sm text-green-400">
                  Ihr Passwort wurde erfolgreich zurückgesetzt! Sie können sich jetzt mit Ihrem neuen Passwort anmelden.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate('/login')}
              className="flex w-full justify-center items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm/6 font-semibold text-gray-900 hover:bg-brand-hover transition-colors"
            >
              Zur Anmeldung
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-sm space-y-10">
        {/* Logo & Header */}
        <div>
          <Link to="/" aria-label="Zur Startseite" className="block">
            <img
              alt="Renditefuchs"
              src={logoUrl}
              className="mx-auto h-10 w-auto"
            />
          </Link>
          <h2 className="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900 dark:text-white">
            Neues Passwort setzen
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off" noValidate>
          {/* Error Message */}
          {formErrors.general && (
            <div className="p-4 rounded-lg bg-status-error/10 border border-red-500/50">
              <p className="text-sm text-red-400">{formErrors.general}</p>
              {(formErrors.general.includes('ungültig') || formErrors.general.includes('abgelaufen')) && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => navigate('/auth/forgot-password')}
                    className="text-sm font-medium text-brand hover:text-brand-hover transition-colors"
                  >
                    Neuen Reset-Link anfordern
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm/6 font-medium text-gray-800 dark:text-gray-100">
              Neues Passwort
            </label>
            <div className="mt-2 relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Lock className="h-5 w-5 text-gray-500" aria-hidden="true" />
              </div>
              <input
                id="password"
                name="new-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Neues Passwort eingeben"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                disabled={isLoading}
                className={`block w-full rounded-md bg-gray-100 dark:bg-white/5 pl-10 pr-10 py-2 text-base text-gray-900 dark:text-white outline-1 -outline-offset-1 placeholder:text-gray-500 focus:outline-2 focus:-outline-offset-2 focus:outline-brand sm:text-sm/6 ${
                  formErrors.password
                    ? 'outline-red-500 focus:outline-red-500'
                    : 'outline-gray-300 dark:outline-white/10'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                tabIndex={-1}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {formErrors.password && (
              <p className="mt-2 text-sm text-red-400">{formErrors.password}</p>
            )}
          </div>

          {/* Confirm Password Field */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm/6 font-medium text-gray-800 dark:text-gray-100">
              Passwort bestätigen
            </label>
            <div className="mt-2 relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Lock className="h-5 w-5 text-gray-500" aria-hidden="true" />
              </div>
              <input
                id="confirmPassword"
                name="confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Passwort wiederholen"
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                disabled={isLoading}
                className={`block w-full rounded-md bg-gray-100 dark:bg-white/5 pl-10 pr-10 py-2 text-base text-gray-900 dark:text-white outline-1 -outline-offset-1 placeholder:text-gray-500 focus:outline-2 focus:-outline-offset-2 focus:outline-brand sm:text-sm/6 ${
                  formErrors.confirmPassword
                    ? 'outline-red-500 focus:outline-red-500'
                    : 'outline-gray-300 dark:outline-white/10'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={isLoading}
                tabIndex={-1}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {formErrors.confirmPassword && (
              <p className="mt-2 text-sm text-red-400">{formErrors.confirmPassword}</p>
            )}
          </div>

          {/* Password Requirements */}
          <div className="p-4 rounded-lg bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-white/10">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Passwort-Anforderungen:</p>
            <ul className="space-y-1">
              <li className={`flex items-center gap-2 text-sm ${
                formData.password.length >= 6
                  ? 'text-status-success'
                  : 'text-gray-500 dark:text-gray-400'
              }`}>
                <Check className={`h-4 w-4 ${formData.password.length >= 6 ? 'opacity-100' : 'opacity-30'}`} />
                Mindestens 6 Zeichen
              </li>
              <li className={`flex items-center gap-2 text-sm ${
                formData.password && formData.confirmPassword && formData.password === formData.confirmPassword
                  ? 'text-status-success'
                  : 'text-gray-500 dark:text-gray-400'
              }`}>
                <Check className={`h-4 w-4 ${
                  formData.password && formData.confirmPassword && formData.password === formData.confirmPassword
                    ? 'opacity-100'
                    : 'opacity-30'
                }`} />
                Passwörter müssen übereinstimmen
              </li>
            </ul>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || !isFormValid()}
            className="flex w-full justify-center items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm/6 font-semibold text-gray-900 hover:bg-brand-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Wird gesetzt...</span>
              </>
            ) : (
              <span>Passwort zurücksetzen</span>
            )}
          </button>

          {/* Back to Login */}
          {onBackToLogin && (
            <p className="mt-10 text-center text-sm/6 text-gray-500 dark:text-gray-400">
              <button
                type="button"
                onClick={onBackToLogin}
                disabled={isLoading}
                className="flex items-center justify-center gap-2 w-full text-brand hover:text-brand-hover transition-colors disabled:opacity-50"
              >
                <ArrowLeft className="h-4 w-4" />
                Zurück zur Anmeldung
              </button>
            </p>
          )}
        </form>
      </div>
    </div>
  );
};
